import React, { useMemo, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { Chip, Icon, IconButton, PageFilterBar, ScopeSelect, SearchInput, StatePanel } from "../../components/ui";
import { ch24ListingId, daysAgo, flagCode, money, moneyIn, regionName, seenLabel, tradeFeedQuery } from "./dealsData";
import { DealCard, DealGrid, DealsInfo, SearchWithInfo } from "./dealsParts";
import { ContactSellerAction } from "./ContactSeller";

// Max 7 days: product-prices:reset (05:00) clears underpriced flags older than that,
// so a longer window could never show anything more.
const DAYS = [
  { key: "1", label: "Last 24 hours" },
  { key: "3", label: "Last 3 days" },
  { key: "7", label: "Last 7 days" },
];
// The price-type chips (server-side `type`), each with the API's count.
const TYPES = [
  { key: "all", label: "All", count: (s) => s.total },
  { key: "trade", label: "WhatsApp trade", count: (s) => s.trade },
  { key: "market", label: "Market", count: (s) => s.market },
];
const SOURCE_LABEL = { trade: "WhatsApp trade", chrono24: "Chrono24", watch_finder: "WatchFinder", watch_collecting: "WatchCollecting", instagram: "Instagram" };

// Sections by the day the price was posted; the biggest gap first inside each.
const DAY_SECTIONS = [
  { value: "today", label: "Today", match: (d) => d === 0 },
  { value: "yesterday", label: "Yesterday", match: (d) => d === 1 },
  { value: "earlier", label: "Earlier this week", match: (d) => d === null || d > 1 },
];

// The ⓘ beside search. Rules: backend DealsReport::belowTrade + the 04:00 underpriced job.
const BELOW_INFO = [
  ["Source", "WhatsApp trade offers + online listings"],
  ["Gets in", "10%+ under the 3-month trade price, in £"],
  ["Checked", "Nightly at 04:00 · kept 7 days"],
  ["Reposts", "Shown once · Posted N×"],
  ["Search", "Reference, brand, model · whole window"],
];

// WhatsApp numbers open a chat; anything that is not a phone number gets no button.
const waLink = (phone) => {
  const digits = String(phone || "").replace(/[^0-9]/g, "");
  return digits.length >= 7 ? `https://wa.me/${digits}` : null;
};

const label = (p) => [p.brand, p.model, p.reference].filter(Boolean).join(" ");

/**
 * One underpriced price on the Stock Alert card via DealCard, like Private sellers and Auctions
 * (Alex, 2026-11-02: "reference, brand, model below image"): the gap, repost and age pills on the
 * photo; reference and brand · model under it; one price line (£ price, trade on the right; the
 * asked currency on hover); the region flag in front of the sender, then round icon actions.
 */
const UnderpricedCard = ({ p }) => {
  const trade = p.kind === "trade";
  const sup = trade ? p.supplier : null;
  const chat = sup ? waLink(sup.phone) : null;
  const pct = Number(p.below_pct);
  const d = daysAgo(p.price_date);
  const flag = flagCode(p.region);
  const foreign = p.currency && p.currency !== "GBP";
  const who = trade
    ? [sup?.name, sup?.company].filter(Boolean).join(" · ") || p.from || "Unknown sender"
    : `${SOURCE_LABEL[p.source] || p.from || "Market"} listing`;
  const whoTitle = trade ? [who, p.from, sup?.phone].filter(Boolean).join("\n") : who;
  const place = p.region ? regionName(p.region) : "Region unknown";

  return (
    <DealCard
      label={label(p)}
      image={p.image}
      badges={<>
        {Number.isFinite(pct) ? (
          <span className={pct >= 20 ? "cl-card__found-trade" : "cl-card__found-online"} title={`${pct.toFixed(1)}% under the 3-month trade median (${money(p.trade_median)})`}>
            −{Math.round(pct)}%
          </span>
        ) : null}
        {p.posted_count > 1 ? <span className="cl-card__found-missed" title={`Same offer reposted · first seen ${seenLabel(p.first_seen)}`}>Posted {p.posted_count}×</span> : null}
        {d !== null ? (
          <span className={d === 0 ? "deals-cl__fresh" : "deals-cl__age"} title={`Posted ${seenLabel(p.price_date)}`}>
            {d === 0 ? "Today" : `${d}d`}
          </span>
        ) : null}
      </>}
      reference={p.reference}
      name={[p.brand, p.model].filter(Boolean).join(" · ")}
      prices={(
        <span className="deals-cl__line">
          <strong title={foreign ? `Asked ${moneyIn(p.price, p.currency)}` : "Asked price"}>{money(p.price_gbp ?? p.price)}</strong>
          <span className="deals-cl__muted" title="DAO trade prices, median of the last 3 months">Trade <strong>{money(p.trade_median)}</strong></span>
        </span>
      )}
      flag={(
        <span className="deals-cl__flag" title={place}>
          {flag ? <ReactCountryFlag countryCode={flag} svg aria-label={place} /> : <Icon name="Globe" size="xs" />}
        </span>
      )}
      who={who}
      whoSub={trade && p.from ? <span className="deals-cl__who-phone" title={whoTitle}>{p.from}</span> : p.region ? <span className="deals-cl__who-phone">{place}</span> : null}
      actions={<>
        {chat ? (
          <IconButton size="sm" round label={`WhatsApp ${sup.phone}`} title={`WhatsApp ${sup.phone}`} href={chat} target="_blank" rel="noopener noreferrer">
            <img src="/assets/icons/whatsapp-green-circle.svg" alt="" width="16" height="16" aria-hidden="true" />
          </IconButton>
        ) : null}
        {ch24ListingId(p.listing_link) ? (
          <ContactSellerAction compact url={p.listing_link} label={label(p)} productId={p.product_id}
            summary={{ image: p.image, price: money(p.price_gbp ?? p.price), meta: `${p.from || "Chrono24"} · ${pct.toFixed(1)}% below trade` }} />
        ) : null}
        {/* Both open in a new tab so the Deals list keeps its place. */}
        {trade
          ? <IconButton size="sm" round label="Open in Trade Feed" title="Open in Trade Feed" href={`/trade-feed?q=${encodeURIComponent(tradeFeedQuery(p.reference))}`} target="_blank" rel="noopener noreferrer"><Icon name="Search" size="sm" /></IconButton>
          : <IconButton size="sm" round label="Open listing" title="Open listing" href={p.listing_link || "#"} target="_blank" rel="noopener noreferrer"><Icon name="ExternalLink" size="sm" /></IconButton>}
      </>}
    />
  );
};

// Market + trade prices 10%+ under the 3M trade median, every SKU (the 04:00 underpriced check),
// re-checked in GBP by the API.
export default function BelowTradeTab({ state, onRetry, days, onDays, type, onType, q, onSearch, region = null, onRegion }) {
  const [text, setText] = useState(q);

  const s = state.data?.summary || {};
  const items = useMemo(() => state.data?.items || [], [state.data]);
  const shownOf = (type === "all" ? s.total : s[type]) || 0;
  // Every region the window has a deal in, busiest first (counts are not narrowed by the region pick).
  const regionCounts = s.regions || {};
  const regions = Object.keys(regionCounts);
  const sections = useMemo(() => DAY_SECTIONS.map((sec) => ({
    ...sec,
    rows: items.filter((p) => sec.match(daysAgo(p.price_date))).sort((a, b) => (Number(b.below_pct) || 0) - (Number(a.below_pct) || 0)),
  })).filter((sec) => sec.rows.length > 0), [items]);

  // Same filter row as Stock Alert (PageFilterBar + SearchInput + ScopeSelect).
  return (
    <div className="deals-stack">
      <PageFilterBar label="Filter underpriced deals"
        search={(
          <SearchWithInfo info={<DealsInfo title="How prices get here" rows={BELOW_INFO} />}>
            <SearchInput
              size="lg"
              placeholder="Search reference, brand or model"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onSearch={onSearch}
              onClear={() => { setText(""); onSearch(""); }}
            />
          </SearchWithInfo>
        )}
      >
        {/* Shown once the API reports regions; a picked region stays listed even at 0. */}
        {onRegion && (regions.length > 0 || region) ? (
          <ScopeSelect
            label="Region"
            placeholder="All regions"
            multiple={false}
            options={(region && !regions.includes(region) ? [...regions, region] : regions)
              .map((r) => ({ key: r, label: `${regionName(r)} ${regionCounts[r] ?? 0}` }))}
            value={region}
            onChange={onRegion}
          />
        ) : null}
        <ScopeSelect
          label="Time window"
          multiple={false}
          options={DAYS}
          value={String(days)}
          onChange={(next) => onDays(Number(next ?? 7))}
        />
      </PageFilterBar>

      <div className="deals-show" role="group" aria-label="Show">
        {TYPES.map((t) => (
          <Chip key={t.key} selected={type === t.key} onClick={() => onType(t.key)} count={typeof t.count(s) === "number" ? t.count(s) : undefined}>
            {t.label}
          </Chip>
        ))}
      </div>
      <p className="deals-note">10%+ under the DAO 3-month trade median, in GBP · checked nightly at 04:00</p>

      {state.status === "loading" ? <StatePanel state="loading" loadingShape="card" loadingCount={6} /> : null}
      {state.status === "error" && !state.data ? <StatePanel state="error" onRetry={onRetry} headingLevel={2} /> : null}
      {state.data && items.length === 0 ? <StatePanel state="no-results" title="Nothing 10% under the trade median in this window." description={null} /> : null}
      {sections.map((sec) => (
        <section key={sec.value} className="deals-stack" aria-labelledby={`bl-${sec.value}`}>
          <h2 id={`bl-${sec.value}`} className="deals-h2">{sec.label} <span className="deals-h2__count">· {sec.rows.length}</span></h2>
          <DealGrid label={`${sec.label} underpriced deals`}>
            {sec.rows.map((p) => <li key={p.id}><UnderpricedCard p={p} /></li>)}
          </DealGrid>
        </section>
      ))}
      {shownOf > items.length ? <p className="deals-note">Showing the newest {items.length} of {shownOf}. Narrow with search, region or the time window.</p> : null}
    </div>
  );
}
